/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApprovalConditionLocationOption } from '../models/ApprovalConditionLocationOption';
import type { ApprovalConditionOption } from '../models/ApprovalConditionOption';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ApprovalsService {

    /**
     * the users an approval rule may name as approvers, with the subject types each may approve
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiApprovalsApproverOptions(): CancelablePromise<{
        /**
         * users holding at least one approve permission, ordered by name
         */
        approvers: Array<{
            external_id: string;
            name: string | null;
            /**
             * the rule subject types this user may approve or reject, which is what InternalApi::Approvals::DecisionsController lets them decide
             */
            subject_types: Array<'OfferLetter' | 'Funnel'>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/approvals/approver_options',
            errors: {
                403: `approval rules are not enabled for this account`,
            },
        });
    }

    /**
     * the fields conditions may be written about, with their types and operators
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiApprovalsFieldCatalog(): CancelablePromise<{
        fields?: Array<{
            key?: string;
            label?: string;
            /**
             * drives both the operator list and which input the builder renders
             */
            type?: 'number' | 'string' | 'date' | 'boolean';
            /**
             * the operators this field's type allows; anything else is rejected on save
             */
            operators?: Array<string>;
            /**
             * non-empty makes the field an enumeration — the builder renders a select
             */
            allowed_values?: Array<string>;
            /**
             * keys this field may be compared against (same type, never itself). Computed here rather than in the client so it cannot disagree with what ConditionsValidator enforces on save.
             */
            comparable_keys?: Array<string>;
        }>;
        /**
         * operators that take no right-hand side. Shipped rather than restated in the client so a new unary operator cannot make the builder demand an operand for a condition the server accepts.
         */
        unary_operators?: Array<string>;
        /**
         * what may be authored per subject type — one entry per Approvals::Rule subject_type, so the Offers | Openings toggle does not refetch
         */
        subjects?: Array<{
            subject_type?: 'OfferLetter' | 'Funnel';
            /**
             * condition types this subject may be gated on, in the order the builder offers them. A type that can never apply to this subject is absent rather than listed as unavailable.
             */
            condition_types?: Array<{
                type?: string;
                /**
                 * where this type may be used — a rule's scope, a level, or both. Empty means it cannot be authored yet, and unavailable_reason says why.
                 */
                scopes?: Array<'scope' | 'level'>;
                /**
                 * why the type is offered but not authorable. A token, not a sentence: the copy lives in the client.
                 */
                unavailable_reason?: string;
            }>;
            /**
             * the attributes a condition may be written about: Opening Owner, then the account's own attribute types, in the same typed shape as `fields`
             */
            attributes?: Array<{
                key?: string;
                label?: string;
                type?: 'number' | 'string' | 'date' | 'boolean';
                operators?: Array<string>;
                allowed_values?: Array<string>;
                /**
                 * what the picker shows for an allowed value a person cannot read, such as a user's external_id; a value it omits reads as itself
                 */
                value_labels?: Record<string, string>;
            }>;
        }>;
        /**
         * condition types that may appear at most once per condition set. Enforced on save, so the builder withholds a second one rather than letting the whole rule be refused.
         */
        single_use_types?: Array<string>;
        /**
         * the operators a compensation condition may use
         */
        comparison_operators?: Array<string>;
        /**
         * whether numeric offer fields may offer the opening maximum as a comparison operand
         */
        opening_pay_range_enabled?: boolean;
        /**
         * which entity an attribute condition reads its value from
         */
        attribute_scopes?: Array<string>;
        /**
         * the attributes the evaluation context can actually read, by scope. An attribute outside this list is refused in a rule's scope, where an unevaluatable condition silently drops the rule.
         */
        sourced_attributes?: Array<{
            scope?: string;
            attributes?: Array<string>;
        }>;
        /**
         * the entities a set-membership condition chooses from. Positions and locations are keyed by name and openings and location groups by external_id, matching what each condition stores. A location carries the group it sits in, which the locations picker groups under; location groups are also a scoping target in their own right, so they are a peer list rather than nested under their locations.
         */
        options?: {
            positions?: Array<ApprovalConditionOption>;
            locations?: Array<ApprovalConditionLocationOption>;
            openings?: Array<ApprovalConditionOption>;
            location_groups?: Array<ApprovalConditionOption>;
        };
        /**
         * option lists the per-request cap cut short. The builder says so under the affected picker, so a short list is never read as a complete one.
         */
        truncated_options?: Array<string>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/approvals/field_catalog',
            errors: {
                403: `approval rules are not enabled for this account`,
            },
        });
    }

    /**
     * list approval requests
     * The requests the caller approves or submitted. An account admin sees the whole account.
     * @param status
     * @param subjectType
     * @param mine only requests the caller can act on right now, derived from active steps
     * @param search free text matched against the opening title and, for an offer, the applicant name. Case-insensitive substring; `%` and `_` are literal.
     * @param page
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiApprovalsRequests(
        status?: 'pending' | 'approved' | 'rejected' | 'cancelled',
        subjectType?: 'OfferLetter' | 'Funnel',
        mine?: boolean,
        search?: string,
        page?: number,
    ): CancelablePromise<{
        requests?: Array<{
            /**
             * the UUID every route and the approver email address this by
             */
            external_id?: string;
            subject_type?: 'OfferLetter' | 'Funnel';
            /**
             * the subject's external_id
             */
            subject_id?: string | null;
            status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
            submitted_at?: string | null;
            decided_at?: string | null;
            rule_groups?: Array<{
                /**
                 * from the pinned definition_snapshot. Null for the bucket holding steps the snapshot cannot attribute to a rule.
                 */
                rule_id?: number | null;
                /**
                 * from the snapshot, never the live rule — a rule edited after submission still groups its own steps the way it did at submission
                 */
                name?: string | null;
                levels?: Array<{
                    /**
                     * addresses this level when posting a decision. Scoped by the request in the route. NULL on a `carried` level, which has no step on this request — the approval it keeps was given against a step of an earlier one, and posting to that id would name a level of a different request.
                     */
                    step_id?: number | null;
                    /**
                     * 1-based WITHIN ITS RULE, and therefore not unique across the request — two rules that both match a subject each have a level at position 1
                     */
                    position?: number;
                    name?: string | null;
                    /**
                     * `skipped` carries two meanings and `condition_met` tells them apart: a conditional level that did not match reads `condition_met: false`, while a level closed because another rule was rejected does not. Neither is "these approvers declined".
                     */
                    status?: 'pending' | 'active' | 'approved' | 'rejected' | 'skipped';
                    /**
                     * on a `carried` level, the logic of the earlier step that gave the approval; null when that step cannot be found
                     */
                    approver_logic?: 'any' | 'all' | null;
                    /**
                     * null when the level carried no condition at all, which is not the same as false
                     */
                    condition_met?: boolean | null;
                    activated_at?: string | null;
                    decided_at?: string | null;
                    /**
                     * when backups are brought in; null if no timer
                     */
                    escalate_at?: string | null;
                    /**
                     * set once backups are in play
                     */
                    escalated_at?: string | null;
                    /**
                     * the level is open because nobody could judge its condition, not because it matched (AXHE-4504). Both carry `condition_met: true`, so this is the only thing separating them.
                     */
                    condition_unevaluatable?: boolean;
                    /**
                     * an open level that can never clear — it resolved to nobody, or to backups with no primary. Blocks rather than auto-passing, and is surfaced so it reads as a misconfiguration rather than a request that quietly stopped moving.
                     */
                    needs_attention?: boolean;
                    /**
                     * an open level that cannot clear until somebody replaces or removes an approver whose `blocked_reason` is `removed`: the approvers still able to act fall short of what its `approver_logic` needs. Backups count once the level has escalated or has an `escalate_at` that will escalate it. False on a request that is no longer pending.
                     */
                    blocked_by_removed_approver?: boolean;
                    approvers?: Array<{
                        /**
                         * User external_id (UUID)
                         */
                        user_id?: string | null;
                        /**
                         * display only; survives a soft-deleted user
                         */
                        name?: string | null;
                        /**
                         * the named role this person was resolved through, from `resolved_from`. Null for a user named directly on the rule.
                         */
                        role?: string | null;
                        /**
                         * a backup is not in play until the level has escalated — `escalated_at` says whether it has
                         */
                        backup?: boolean;
                        status?: 'pending' | 'approved' | 'rejected';
                        /**
                         * from the audit row; null until this person acts
                         */
                        decided_at?: string | null;
                        /**
                         * display only; survives a soft-deleted user. Null for a caller the request does not involve — see `subject`.
                         */
                        email?: string | null;
                        /**
                         * what this person said, from the immutable decision row. Present on a rejection for a caller the request involves, or an account admin — the reason is mandatory and goes back to whoever sent the offer. Null for anyone else, on the same gate as `subject`; `status` still reads `rejected`.
                         */
                        comment?: string | null;
                        /**
                         * whether this row is the caller. Distinguishes "you have already responded" from "a colleague responded and your level is not open yet" — two states with the same absence of controls and opposite explanations.
                         */
                        is_viewer?: boolean;
                        /**
                         * why this person cannot act on an assignment that still waits on them. `removed`: they left the account, and only a replacement unblocks the assignment. `no_permission`: they no longer hold the approve permission for this subject type, so the decisions endpoint refuses them; granting it back unblocks them. Null once they have decided, on a request that is no longer pending, and for anyone who can act.
                         */
                        blocked_reason?: 'removed' | 'no_permission' | null;
                    }>;
                    /**
                     * this level was not asked on this request. An edit of an approved subject asks again only the levels that read something it changed; the rest keep the approval they hold and are rendered here, in position order, so the ladder is the whole ladder rather than the part of it that was re-asked. A carried level reads `approved` with no `step_id`. Its approvers and its activated, decided and escalated times are those of the step on the earlier request that gave the approval, which `previous_submissions` lists. Each approver `email` and `comment` follows the visibility of that earlier request. Every level of an exempt request is carried: it shows the ladder of the approval the edit followed, unchanged.
                     */
                    carried?: boolean;
                }>;
            }>;
            /**
             * what this request is about, shaped by what kind of thing it is about — `subject_id` is a UUID and nothing else names the subject. An OFFER carries all of the fields below, beside `subject_id` rather than instead of it, because internal_api/offer_letters/:external_id runs authorize_applicant! and 403s an approver with no brand or location visibility on the applicant — which is most of the people this is served to. An OPENING carries `position_name` alone: it shares the offer's key rather than getting one of its own, so a client renders the opening from one field whatever the subject is. NULL for a caller the request does not involve: `show` is scoped to the account family and no further, and `document_url` is a two-day bearer link to a named person's compensation letter. The gate is the same `involving`-unless-admin rule the index applies to the list that leads here.
             */
            subject?: {
                /**
                 * offers only
                 */
                applicant_name?: string | null;
                /**
                 * the opening. On an offer, the applicant's opening's position, falling back to that opening's own title; on an opening request, the subject's own title.
                 */
                position_name?: string | null;
                /**
                 * offers only
                 */
                document_title?: string | null;
                /**
                 * offers only; the rendered letter, presigned for two days. Null until it has been rendered.
                 */
                document_url?: string | null;
                /**
                 * offers only; the offer's own status, not the request's
                 */
                offer_status?: string | null;
                /**
                 * offers only; the applicant the offer is for, as the offer_letter_form endpoint addresses them. What the review panel's edit form is fetched with (AXHE-4759).
                 */
                applicant_id?: string | null;
                /**
                 * offers only; the template this offer was generated from. Passed to the form so it resolves the document under review rather than the stage's default, which differ when a stage holds several.
                 */
                offer_letter_template_id?: string | null;
                /**
                 * offers only; what the offer currently says, by template field key. The form endpoint cannot supply these — it pre-fills from FindResumableOffer, which excludes the pending_approval and approved offers an approver edits from here. ABSENT on the index, the way previous_submissions is empty there: no queue row renders a salary, and a page of 25 would carry 25 offers' compensation for nothing.
                 */
                field_values?: Record<string, string>;
                /**
                 * offers only, show only; the answers driving the template's conditional sections, by data key. A separate key space from field_values.
                 */
                condition_values?: Record<string, string>;
                /**
                 * offers only, show only; which controls the offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so the form resolves them under this report.
                 */
                edited_keys?: {
                    /**
                     * template field keys
                     */
                    field_values?: Array<string>;
                    /**
                     * condition data keys
                     */
                    condition_values?: Array<string>;
                };
            } | null;
            /**
             * openings only; the edit a live opening is holding until this request is decided, one entry per changed field. Empty for a publication, which gates the opening itself and holds nothing beside it, and empty for a caller the request does not involve — same gate as `subject`. Approving applies every `to` to the opening; rejecting discards them and the opening keeps serving the `from` values it has served throughout.
             */
            proposed_changes?: Array<{
                /**
                 * the opening attribute, e.g. `title` or `location_id`
                 */
                field?: string;
                /**
                 * the attribute named as a recruiter edits it, e.g. `Location`
                 */
                label?: string;
                /**
                 * the value the opening is serving now, rendered for display
                 */
                from?: string;
                /**
                 * the value proposed, rendered for display
                 */
                to?: string;
            }>;
            /**
             * what happened to this offer before this request, newest first. A resubmission is a fresh request with fresh steps, so every level reads pending again — this is the only thing telling an approver they are being asked a second time, and why it came back. ALWAYS EMPTY on the index: the ancestry is a primary-key walk plus a decisions read per ancestor, and nothing on a list row shows history.
             */
            previous_submissions?: Array<{
                /**
                 * 1-based from the oldest attempt
                 */
                number?: number;
                status?: 'approved' | 'rejected' | 'cancelled';
                submitted_at?: string | null;
                decided_at?: string | null;
                /**
                 * null when it was not rejected, and null throughout for a caller the request does not involve — same gate as `subject`. `status` still reads `rejected`.
                 */
                rejection_reason?: string | null;
            }>;
            viewer?: {
                /**
                 * the levels the caller may decide right now. Gate approve/reject on this rather than on `status` and `backup`. It checks the decision endpoint's identity rules but not the approve permission, so an assigned approver whose permission was revoked still gets the step listed and a 403 on deciding it. Empty once the request is settled, `cancelled` included: a cancelled request keeps its steps as the record of what happened, so an open-looking step can outlive it. Empty too for an impersonated session, which the decision endpoint refuses with 403 — an approval has to be the approver's own act.
                 */
                actionable_step_ids?: Array<number>;
                /**
                 * offers only, show only; whether the offer endpoints would accept this caller — ApplicantPolicy#manage?, which internal_api/offer_letters and internal_api/applicants/:id/offer_letter_form both gate on. NOT "may they edit": that also needs the edit permission, which a client ANDs itself. Published because an approver frequently fails applicant visibility — the same mismatch the `subject` block exists to work around for reading — so a client gating an edit control on the permission alone renders one that 403s when pressed (AXHE-4759).
                 */
                can_manage_offer?: boolean;
            };
        }>;
        pagination?: {
            current_page?: number;
            next_page?: number | null;
            per_page?: number;
            total_count?: number;
            total_pages?: number;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/approvals/requests',
            query: {
                'status': status,
                'subject_type': subjectType,
                'mine': mine,
                'search': search,
                'page': page,
            },
            errors: {
                422: `a filter value outside its enum`,
            },
        });
    }

    /**
     * fetch one approval request
     * @param id request external_id (UUID) — what the approver notification email links to
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiApprovalsRequests1(
        id: string,
    ): CancelablePromise<{
        request?: {
            /**
             * the UUID every route and the approver email address this by
             */
            external_id?: string;
            subject_type?: 'OfferLetter' | 'Funnel';
            /**
             * the subject's external_id
             */
            subject_id?: string | null;
            status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
            submitted_at?: string | null;
            decided_at?: string | null;
            rule_groups?: Array<{
                /**
                 * from the pinned definition_snapshot. Null for the bucket holding steps the snapshot cannot attribute to a rule.
                 */
                rule_id?: number | null;
                /**
                 * from the snapshot, never the live rule — a rule edited after submission still groups its own steps the way it did at submission
                 */
                name?: string | null;
                levels?: Array<{
                    /**
                     * addresses this level when posting a decision. Scoped by the request in the route. NULL on a `carried` level, which has no step on this request — the approval it keeps was given against a step of an earlier one, and posting to that id would name a level of a different request.
                     */
                    step_id?: number | null;
                    /**
                     * 1-based WITHIN ITS RULE, and therefore not unique across the request — two rules that both match a subject each have a level at position 1
                     */
                    position?: number;
                    name?: string | null;
                    /**
                     * `skipped` carries two meanings and `condition_met` tells them apart: a conditional level that did not match reads `condition_met: false`, while a level closed because another rule was rejected does not. Neither is "these approvers declined".
                     */
                    status?: 'pending' | 'active' | 'approved' | 'rejected' | 'skipped';
                    /**
                     * on a `carried` level, the logic of the earlier step that gave the approval; null when that step cannot be found
                     */
                    approver_logic?: 'any' | 'all' | null;
                    /**
                     * null when the level carried no condition at all, which is not the same as false
                     */
                    condition_met?: boolean | null;
                    activated_at?: string | null;
                    decided_at?: string | null;
                    /**
                     * when backups are brought in; null if no timer
                     */
                    escalate_at?: string | null;
                    /**
                     * set once backups are in play
                     */
                    escalated_at?: string | null;
                    /**
                     * the level is open because nobody could judge its condition, not because it matched (AXHE-4504). Both carry `condition_met: true`, so this is the only thing separating them.
                     */
                    condition_unevaluatable?: boolean;
                    /**
                     * an open level that can never clear — it resolved to nobody, or to backups with no primary. Blocks rather than auto-passing, and is surfaced so it reads as a misconfiguration rather than a request that quietly stopped moving.
                     */
                    needs_attention?: boolean;
                    /**
                     * an open level that cannot clear until somebody replaces or removes an approver whose `blocked_reason` is `removed`: the approvers still able to act fall short of what its `approver_logic` needs. Backups count once the level has escalated or has an `escalate_at` that will escalate it. False on a request that is no longer pending.
                     */
                    blocked_by_removed_approver?: boolean;
                    approvers?: Array<{
                        /**
                         * User external_id (UUID)
                         */
                        user_id?: string | null;
                        /**
                         * display only; survives a soft-deleted user
                         */
                        name?: string | null;
                        /**
                         * the named role this person was resolved through, from `resolved_from`. Null for a user named directly on the rule.
                         */
                        role?: string | null;
                        /**
                         * a backup is not in play until the level has escalated — `escalated_at` says whether it has
                         */
                        backup?: boolean;
                        status?: 'pending' | 'approved' | 'rejected';
                        /**
                         * from the audit row; null until this person acts
                         */
                        decided_at?: string | null;
                        /**
                         * display only; survives a soft-deleted user. Null for a caller the request does not involve — see `subject`.
                         */
                        email?: string | null;
                        /**
                         * what this person said, from the immutable decision row. Present on a rejection for a caller the request involves, or an account admin — the reason is mandatory and goes back to whoever sent the offer. Null for anyone else, on the same gate as `subject`; `status` still reads `rejected`.
                         */
                        comment?: string | null;
                        /**
                         * whether this row is the caller. Distinguishes "you have already responded" from "a colleague responded and your level is not open yet" — two states with the same absence of controls and opposite explanations.
                         */
                        is_viewer?: boolean;
                        /**
                         * why this person cannot act on an assignment that still waits on them. `removed`: they left the account, and only a replacement unblocks the assignment. `no_permission`: they no longer hold the approve permission for this subject type, so the decisions endpoint refuses them; granting it back unblocks them. Null once they have decided, on a request that is no longer pending, and for anyone who can act.
                         */
                        blocked_reason?: 'removed' | 'no_permission' | null;
                    }>;
                    /**
                     * this level was not asked on this request. An edit of an approved subject asks again only the levels that read something it changed; the rest keep the approval they hold and are rendered here, in position order, so the ladder is the whole ladder rather than the part of it that was re-asked. A carried level reads `approved` with no `step_id`. Its approvers and its activated, decided and escalated times are those of the step on the earlier request that gave the approval, which `previous_submissions` lists. Each approver `email` and `comment` follows the visibility of that earlier request. Every level of an exempt request is carried: it shows the ladder of the approval the edit followed, unchanged.
                     */
                    carried?: boolean;
                }>;
            }>;
            /**
             * what this request is about, shaped by what kind of thing it is about — `subject_id` is a UUID and nothing else names the subject. An OFFER carries all of the fields below, beside `subject_id` rather than instead of it, because internal_api/offer_letters/:external_id runs authorize_applicant! and 403s an approver with no brand or location visibility on the applicant — which is most of the people this is served to. An OPENING carries `position_name` alone: it shares the offer's key rather than getting one of its own, so a client renders the opening from one field whatever the subject is. NULL for a caller the request does not involve: `show` is scoped to the account family and no further, and `document_url` is a two-day bearer link to a named person's compensation letter. The gate is the same `involving`-unless-admin rule the index applies to the list that leads here.
             */
            subject?: {
                /**
                 * offers only
                 */
                applicant_name?: string | null;
                /**
                 * the opening. On an offer, the applicant's opening's position, falling back to that opening's own title; on an opening request, the subject's own title.
                 */
                position_name?: string | null;
                /**
                 * offers only
                 */
                document_title?: string | null;
                /**
                 * offers only; the rendered letter, presigned for two days. Null until it has been rendered.
                 */
                document_url?: string | null;
                /**
                 * offers only; the offer's own status, not the request's
                 */
                offer_status?: string | null;
                /**
                 * offers only; the applicant the offer is for, as the offer_letter_form endpoint addresses them. What the review panel's edit form is fetched with (AXHE-4759).
                 */
                applicant_id?: string | null;
                /**
                 * offers only; the template this offer was generated from. Passed to the form so it resolves the document under review rather than the stage's default, which differ when a stage holds several.
                 */
                offer_letter_template_id?: string | null;
                /**
                 * offers only; what the offer currently says, by template field key. The form endpoint cannot supply these — it pre-fills from FindResumableOffer, which excludes the pending_approval and approved offers an approver edits from here. ABSENT on the index, the way previous_submissions is empty there: no queue row renders a salary, and a page of 25 would carry 25 offers' compensation for nothing.
                 */
                field_values?: Record<string, string>;
                /**
                 * offers only, show only; the answers driving the template's conditional sections, by data key. A separate key space from field_values.
                 */
                condition_values?: Record<string, string>;
                /**
                 * offers only, show only; which controls the offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so the form resolves them under this report.
                 */
                edited_keys?: {
                    /**
                     * template field keys
                     */
                    field_values?: Array<string>;
                    /**
                     * condition data keys
                     */
                    condition_values?: Array<string>;
                };
            } | null;
            /**
             * openings only; the edit a live opening is holding until this request is decided, one entry per changed field. Empty for a publication, which gates the opening itself and holds nothing beside it, and empty for a caller the request does not involve — same gate as `subject`. Approving applies every `to` to the opening; rejecting discards them and the opening keeps serving the `from` values it has served throughout.
             */
            proposed_changes?: Array<{
                /**
                 * the opening attribute, e.g. `title` or `location_id`
                 */
                field?: string;
                /**
                 * the attribute named as a recruiter edits it, e.g. `Location`
                 */
                label?: string;
                /**
                 * the value the opening is serving now, rendered for display
                 */
                from?: string;
                /**
                 * the value proposed, rendered for display
                 */
                to?: string;
            }>;
            /**
             * what happened to this offer before this request, newest first. A resubmission is a fresh request with fresh steps, so every level reads pending again — this is the only thing telling an approver they are being asked a second time, and why it came back. ALWAYS EMPTY on the index: the ancestry is a primary-key walk plus a decisions read per ancestor, and nothing on a list row shows history.
             */
            previous_submissions?: Array<{
                /**
                 * 1-based from the oldest attempt
                 */
                number?: number;
                status?: 'approved' | 'rejected' | 'cancelled';
                submitted_at?: string | null;
                decided_at?: string | null;
                /**
                 * null when it was not rejected, and null throughout for a caller the request does not involve — same gate as `subject`. `status` still reads `rejected`.
                 */
                rejection_reason?: string | null;
            }>;
            viewer?: {
                /**
                 * the levels the caller may decide right now. Gate approve/reject on this rather than on `status` and `backup`. It checks the decision endpoint's identity rules but not the approve permission, so an assigned approver whose permission was revoked still gets the step listed and a 403 on deciding it. Empty once the request is settled, `cancelled` included: a cancelled request keeps its steps as the record of what happened, so an open-looking step can outlive it. Empty too for an impersonated session, which the decision endpoint refuses with 403 — an approval has to be the approver's own act.
                 */
                actionable_step_ids?: Array<number>;
                /**
                 * offers only, show only; whether the offer endpoints would accept this caller — ApplicantPolicy#manage?, which internal_api/offer_letters and internal_api/applicants/:id/offer_letter_form both gate on. NOT "may they edit": that also needs the edit permission, which a client ANDs itself. Published because an approver frequently fails applicant visibility — the same mismatch the `subject` block exists to work around for reading — so a client gating an edit control on the permission alone renders one that 403s when pressed (AXHE-4759).
                 */
                can_manage_offer?: boolean;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/approvals/requests/{id}',
            path: {
                'id': id,
            },
            errors: {
                404: `no such request in this account`,
            },
        });
    }

    /**
     * approve or reject one level
     * @param requestId request external_id (UUID)
     * @param requestBody
     * @returns any the decision is recorded and the request advances
     * @throws ApiError
     */
    public static postInternalApiApprovalsRequestsDecisions(
        requestId: string,
        requestBody: {
            /**
             * the level being decided, from `rule_groups[].levels[].step_id`
             */
            step_id: number;
            decision: 'approved' | 'rejected';
            /**
             * recorded on the append-only audit row. REQUIRED on a rejection — it is the reason the resubmitting recruiter reads in the submission history, and a rejection sent without one is refused with 422. Optional on an approval. Text only: a value of any other type is treated as absent.
             */
            comment?: string;
        },
    ): CancelablePromise<{
        decision?: {
            id?: number;
            /**
             * null on an automated decision — nobody decided it
             */
            step_id?: number | null;
            decision?: 'approved' | 'rejected';
            comment?: string | null;
            /**
             * true when the engine recorded an approval no person made
             */
            automated?: boolean;
            decided_at?: string;
            user_id?: string | null;
            user_name?: string | null;
        };
        request?: {
            /**
             * the UUID every route and the approver email address this by
             */
            external_id?: string;
            subject_type?: 'OfferLetter' | 'Funnel';
            /**
             * the subject's external_id
             */
            subject_id?: string | null;
            status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
            submitted_at?: string | null;
            decided_at?: string | null;
            rule_groups?: Array<{
                /**
                 * from the pinned definition_snapshot. Null for the bucket holding steps the snapshot cannot attribute to a rule.
                 */
                rule_id?: number | null;
                /**
                 * from the snapshot, never the live rule — a rule edited after submission still groups its own steps the way it did at submission
                 */
                name?: string | null;
                levels?: Array<{
                    /**
                     * addresses this level when posting a decision. Scoped by the request in the route. NULL on a `carried` level, which has no step on this request — the approval it keeps was given against a step of an earlier one, and posting to that id would name a level of a different request.
                     */
                    step_id?: number | null;
                    /**
                     * 1-based WITHIN ITS RULE, and therefore not unique across the request — two rules that both match a subject each have a level at position 1
                     */
                    position?: number;
                    name?: string | null;
                    /**
                     * `skipped` carries two meanings and `condition_met` tells them apart: a conditional level that did not match reads `condition_met: false`, while a level closed because another rule was rejected does not. Neither is "these approvers declined".
                     */
                    status?: 'pending' | 'active' | 'approved' | 'rejected' | 'skipped';
                    /**
                     * on a `carried` level, the logic of the earlier step that gave the approval; null when that step cannot be found
                     */
                    approver_logic?: 'any' | 'all' | null;
                    /**
                     * null when the level carried no condition at all, which is not the same as false
                     */
                    condition_met?: boolean | null;
                    activated_at?: string | null;
                    decided_at?: string | null;
                    /**
                     * when backups are brought in; null if no timer
                     */
                    escalate_at?: string | null;
                    /**
                     * set once backups are in play
                     */
                    escalated_at?: string | null;
                    /**
                     * the level is open because nobody could judge its condition, not because it matched (AXHE-4504). Both carry `condition_met: true`, so this is the only thing separating them.
                     */
                    condition_unevaluatable?: boolean;
                    /**
                     * an open level that can never clear — it resolved to nobody, or to backups with no primary. Blocks rather than auto-passing, and is surfaced so it reads as a misconfiguration rather than a request that quietly stopped moving.
                     */
                    needs_attention?: boolean;
                    /**
                     * an open level that cannot clear until somebody replaces or removes an approver whose `blocked_reason` is `removed`: the approvers still able to act fall short of what its `approver_logic` needs. Backups count once the level has escalated or has an `escalate_at` that will escalate it. False on a request that is no longer pending.
                     */
                    blocked_by_removed_approver?: boolean;
                    approvers?: Array<{
                        /**
                         * User external_id (UUID)
                         */
                        user_id?: string | null;
                        /**
                         * display only; survives a soft-deleted user
                         */
                        name?: string | null;
                        /**
                         * the named role this person was resolved through, from `resolved_from`. Null for a user named directly on the rule.
                         */
                        role?: string | null;
                        /**
                         * a backup is not in play until the level has escalated — `escalated_at` says whether it has
                         */
                        backup?: boolean;
                        status?: 'pending' | 'approved' | 'rejected';
                        /**
                         * from the audit row; null until this person acts
                         */
                        decided_at?: string | null;
                        /**
                         * display only; survives a soft-deleted user. Null for a caller the request does not involve — see `subject`.
                         */
                        email?: string | null;
                        /**
                         * what this person said, from the immutable decision row. Present on a rejection for a caller the request involves, or an account admin — the reason is mandatory and goes back to whoever sent the offer. Null for anyone else, on the same gate as `subject`; `status` still reads `rejected`.
                         */
                        comment?: string | null;
                        /**
                         * whether this row is the caller. Distinguishes "you have already responded" from "a colleague responded and your level is not open yet" — two states with the same absence of controls and opposite explanations.
                         */
                        is_viewer?: boolean;
                        /**
                         * why this person cannot act on an assignment that still waits on them. `removed`: they left the account, and only a replacement unblocks the assignment. `no_permission`: they no longer hold the approve permission for this subject type, so the decisions endpoint refuses them; granting it back unblocks them. Null once they have decided, on a request that is no longer pending, and for anyone who can act.
                         */
                        blocked_reason?: 'removed' | 'no_permission' | null;
                    }>;
                    /**
                     * this level was not asked on this request. An edit of an approved subject asks again only the levels that read something it changed; the rest keep the approval they hold and are rendered here, in position order, so the ladder is the whole ladder rather than the part of it that was re-asked. A carried level reads `approved` with no `step_id`. Its approvers and its activated, decided and escalated times are those of the step on the earlier request that gave the approval, which `previous_submissions` lists. Each approver `email` and `comment` follows the visibility of that earlier request. Every level of an exempt request is carried: it shows the ladder of the approval the edit followed, unchanged.
                     */
                    carried?: boolean;
                }>;
            }>;
            /**
             * what this request is about, shaped by what kind of thing it is about — `subject_id` is a UUID and nothing else names the subject. An OFFER carries all of the fields below, beside `subject_id` rather than instead of it, because internal_api/offer_letters/:external_id runs authorize_applicant! and 403s an approver with no brand or location visibility on the applicant — which is most of the people this is served to. An OPENING carries `position_name` alone: it shares the offer's key rather than getting one of its own, so a client renders the opening from one field whatever the subject is. NULL for a caller the request does not involve: `show` is scoped to the account family and no further, and `document_url` is a two-day bearer link to a named person's compensation letter. The gate is the same `involving`-unless-admin rule the index applies to the list that leads here.
             */
            subject?: {
                /**
                 * offers only
                 */
                applicant_name?: string | null;
                /**
                 * the opening. On an offer, the applicant's opening's position, falling back to that opening's own title; on an opening request, the subject's own title.
                 */
                position_name?: string | null;
                /**
                 * offers only
                 */
                document_title?: string | null;
                /**
                 * offers only; the rendered letter, presigned for two days. Null until it has been rendered.
                 */
                document_url?: string | null;
                /**
                 * offers only; the offer's own status, not the request's
                 */
                offer_status?: string | null;
                /**
                 * offers only; the applicant the offer is for, as the offer_letter_form endpoint addresses them. What the review panel's edit form is fetched with (AXHE-4759).
                 */
                applicant_id?: string | null;
                /**
                 * offers only; the template this offer was generated from. Passed to the form so it resolves the document under review rather than the stage's default, which differ when a stage holds several.
                 */
                offer_letter_template_id?: string | null;
                /**
                 * offers only; what the offer currently says, by template field key. The form endpoint cannot supply these — it pre-fills from FindResumableOffer, which excludes the pending_approval and approved offers an approver edits from here. ABSENT on the index, the way previous_submissions is empty there: no queue row renders a salary, and a page of 25 would carry 25 offers' compensation for nothing.
                 */
                field_values?: Record<string, string>;
                /**
                 * offers only, show only; the answers driving the template's conditional sections, by data key. A separate key space from field_values.
                 */
                condition_values?: Record<string, string>;
                /**
                 * offers only, show only; which controls the offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so the form resolves them under this report.
                 */
                edited_keys?: {
                    /**
                     * template field keys
                     */
                    field_values?: Array<string>;
                    /**
                     * condition data keys
                     */
                    condition_values?: Array<string>;
                };
            } | null;
            /**
             * openings only; the edit a live opening is holding until this request is decided, one entry per changed field. Empty for a publication, which gates the opening itself and holds nothing beside it, and empty for a caller the request does not involve — same gate as `subject`. Approving applies every `to` to the opening; rejecting discards them and the opening keeps serving the `from` values it has served throughout.
             */
            proposed_changes?: Array<{
                /**
                 * the opening attribute, e.g. `title` or `location_id`
                 */
                field?: string;
                /**
                 * the attribute named as a recruiter edits it, e.g. `Location`
                 */
                label?: string;
                /**
                 * the value the opening is serving now, rendered for display
                 */
                from?: string;
                /**
                 * the value proposed, rendered for display
                 */
                to?: string;
            }>;
            /**
             * what happened to this offer before this request, newest first. A resubmission is a fresh request with fresh steps, so every level reads pending again — this is the only thing telling an approver they are being asked a second time, and why it came back. ALWAYS EMPTY on the index: the ancestry is a primary-key walk plus a decisions read per ancestor, and nothing on a list row shows history.
             */
            previous_submissions?: Array<{
                /**
                 * 1-based from the oldest attempt
                 */
                number?: number;
                status?: 'approved' | 'rejected' | 'cancelled';
                submitted_at?: string | null;
                decided_at?: string | null;
                /**
                 * null when it was not rejected, and null throughout for a caller the request does not involve — same gate as `subject`. `status` still reads `rejected`.
                 */
                rejection_reason?: string | null;
            }>;
            viewer?: {
                /**
                 * the levels the caller may decide right now. Gate approve/reject on this rather than on `status` and `backup`. It checks the decision endpoint's identity rules but not the approve permission, so an assigned approver whose permission was revoked still gets the step listed and a 403 on deciding it. Empty once the request is settled, `cancelled` included: a cancelled request keeps its steps as the record of what happened, so an open-looking step can outlive it. Empty too for an impersonated session, which the decision endpoint refuses with 403 — an approval has to be the approver's own act.
                 */
                actionable_step_ids?: Array<number>;
                /**
                 * offers only, show only; whether the offer endpoints would accept this caller — ApplicantPolicy#manage?, which internal_api/offer_letters and internal_api/applicants/:id/offer_letter_form both gate on. NOT "may they edit": that also needs the edit permission, which a client ANDs itself. Published because an approver frequently fails applicant visibility — the same mismatch the `subject` block exists to work around for reading — so a client gating an edit control on the permission alone renders one that 403s when pressed (AXHE-4759).
                 */
                can_manage_offer?: boolean;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/approvals/requests/{request_id}/decisions',
            path: {
                'request_id': requestId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `the caller is not an approver on this level, is a backup before it escalated, lacks the approve permission for this subject type, or is impersonating the approver`,
                404: `no such request in this account, or the step belongs to another request`,
                409: `this approver has already acted, or the level or request settled first`,
                422: `the decision is neither approved nor rejected, or it is a rejection carrying no reason`,
            },
        });
    }

    /**
     * replace or remove an approver who left the account
     * For a caller holding the rule-management permission. Changes this request only: the rule is not edited. Applies only to an approver whose `blocked_reason` is `removed`; one who lost the approve permission waits for it to be granted back.
     * @param requestId request external_id (UUID)
     * @param requestBody
     * @returns any the change is recorded and the request advances
     * @throws ApiError
     */
    public static postInternalApiApprovalsRequestsApproverChanges(
        requestId: string,
        requestBody: {
            /**
             * the level, from `rule_groups[].levels[].step_id`
             */
            step_id: number;
            /**
             * the departed approver, from `levels[].approvers[].user_id`
             */
            user_id: string;
            /**
             * who takes their place. They must hold the approve permission for the subject type. Absent or null removes the approver with nobody in their place, which can complete the level when the sign-offs it still needs are all in. A removal is refused when the approver is the level's only primary.
             */
            replacement_user_id?: string | null;
        },
    ): CancelablePromise<{
        request?: {
            /**
             * the UUID every route and the approver email address this by
             */
            external_id?: string;
            subject_type?: 'OfferLetter' | 'Funnel';
            /**
             * the subject's external_id
             */
            subject_id?: string | null;
            status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
            submitted_at?: string | null;
            decided_at?: string | null;
            rule_groups?: Array<{
                /**
                 * from the pinned definition_snapshot. Null for the bucket holding steps the snapshot cannot attribute to a rule.
                 */
                rule_id?: number | null;
                /**
                 * from the snapshot, never the live rule — a rule edited after submission still groups its own steps the way it did at submission
                 */
                name?: string | null;
                levels?: Array<{
                    /**
                     * addresses this level when posting a decision. Scoped by the request in the route. NULL on a `carried` level, which has no step on this request — the approval it keeps was given against a step of an earlier one, and posting to that id would name a level of a different request.
                     */
                    step_id?: number | null;
                    /**
                     * 1-based WITHIN ITS RULE, and therefore not unique across the request — two rules that both match a subject each have a level at position 1
                     */
                    position?: number;
                    name?: string | null;
                    /**
                     * `skipped` carries two meanings and `condition_met` tells them apart: a conditional level that did not match reads `condition_met: false`, while a level closed because another rule was rejected does not. Neither is "these approvers declined".
                     */
                    status?: 'pending' | 'active' | 'approved' | 'rejected' | 'skipped';
                    /**
                     * on a `carried` level, the logic of the earlier step that gave the approval; null when that step cannot be found
                     */
                    approver_logic?: 'any' | 'all' | null;
                    /**
                     * null when the level carried no condition at all, which is not the same as false
                     */
                    condition_met?: boolean | null;
                    activated_at?: string | null;
                    decided_at?: string | null;
                    /**
                     * when backups are brought in; null if no timer
                     */
                    escalate_at?: string | null;
                    /**
                     * set once backups are in play
                     */
                    escalated_at?: string | null;
                    /**
                     * the level is open because nobody could judge its condition, not because it matched (AXHE-4504). Both carry `condition_met: true`, so this is the only thing separating them.
                     */
                    condition_unevaluatable?: boolean;
                    /**
                     * an open level that can never clear — it resolved to nobody, or to backups with no primary. Blocks rather than auto-passing, and is surfaced so it reads as a misconfiguration rather than a request that quietly stopped moving.
                     */
                    needs_attention?: boolean;
                    /**
                     * an open level that cannot clear until somebody replaces or removes an approver whose `blocked_reason` is `removed`: the approvers still able to act fall short of what its `approver_logic` needs. Backups count once the level has escalated or has an `escalate_at` that will escalate it. False on a request that is no longer pending.
                     */
                    blocked_by_removed_approver?: boolean;
                    approvers?: Array<{
                        /**
                         * User external_id (UUID)
                         */
                        user_id?: string | null;
                        /**
                         * display only; survives a soft-deleted user
                         */
                        name?: string | null;
                        /**
                         * the named role this person was resolved through, from `resolved_from`. Null for a user named directly on the rule.
                         */
                        role?: string | null;
                        /**
                         * a backup is not in play until the level has escalated — `escalated_at` says whether it has
                         */
                        backup?: boolean;
                        status?: 'pending' | 'approved' | 'rejected';
                        /**
                         * from the audit row; null until this person acts
                         */
                        decided_at?: string | null;
                        /**
                         * display only; survives a soft-deleted user. Null for a caller the request does not involve — see `subject`.
                         */
                        email?: string | null;
                        /**
                         * what this person said, from the immutable decision row. Present on a rejection for a caller the request involves, or an account admin — the reason is mandatory and goes back to whoever sent the offer. Null for anyone else, on the same gate as `subject`; `status` still reads `rejected`.
                         */
                        comment?: string | null;
                        /**
                         * whether this row is the caller. Distinguishes "you have already responded" from "a colleague responded and your level is not open yet" — two states with the same absence of controls and opposite explanations.
                         */
                        is_viewer?: boolean;
                        /**
                         * why this person cannot act on an assignment that still waits on them. `removed`: they left the account, and only a replacement unblocks the assignment. `no_permission`: they no longer hold the approve permission for this subject type, so the decisions endpoint refuses them; granting it back unblocks them. Null once they have decided, on a request that is no longer pending, and for anyone who can act.
                         */
                        blocked_reason?: 'removed' | 'no_permission' | null;
                    }>;
                    /**
                     * this level was not asked on this request. An edit of an approved subject asks again only the levels that read something it changed; the rest keep the approval they hold and are rendered here, in position order, so the ladder is the whole ladder rather than the part of it that was re-asked. A carried level reads `approved` with no `step_id`. Its approvers and its activated, decided and escalated times are those of the step on the earlier request that gave the approval, which `previous_submissions` lists. Each approver `email` and `comment` follows the visibility of that earlier request. Every level of an exempt request is carried: it shows the ladder of the approval the edit followed, unchanged.
                     */
                    carried?: boolean;
                }>;
            }>;
            /**
             * what this request is about, shaped by what kind of thing it is about — `subject_id` is a UUID and nothing else names the subject. An OFFER carries all of the fields below, beside `subject_id` rather than instead of it, because internal_api/offer_letters/:external_id runs authorize_applicant! and 403s an approver with no brand or location visibility on the applicant — which is most of the people this is served to. An OPENING carries `position_name` alone: it shares the offer's key rather than getting one of its own, so a client renders the opening from one field whatever the subject is. NULL for a caller the request does not involve: `show` is scoped to the account family and no further, and `document_url` is a two-day bearer link to a named person's compensation letter. The gate is the same `involving`-unless-admin rule the index applies to the list that leads here.
             */
            subject?: {
                /**
                 * offers only
                 */
                applicant_name?: string | null;
                /**
                 * the opening. On an offer, the applicant's opening's position, falling back to that opening's own title; on an opening request, the subject's own title.
                 */
                position_name?: string | null;
                /**
                 * offers only
                 */
                document_title?: string | null;
                /**
                 * offers only; the rendered letter, presigned for two days. Null until it has been rendered.
                 */
                document_url?: string | null;
                /**
                 * offers only; the offer's own status, not the request's
                 */
                offer_status?: string | null;
                /**
                 * offers only; the applicant the offer is for, as the offer_letter_form endpoint addresses them. What the review panel's edit form is fetched with (AXHE-4759).
                 */
                applicant_id?: string | null;
                /**
                 * offers only; the template this offer was generated from. Passed to the form so it resolves the document under review rather than the stage's default, which differ when a stage holds several.
                 */
                offer_letter_template_id?: string | null;
                /**
                 * offers only; what the offer currently says, by template field key. The form endpoint cannot supply these — it pre-fills from FindResumableOffer, which excludes the pending_approval and approved offers an approver edits from here. ABSENT on the index, the way previous_submissions is empty there: no queue row renders a salary, and a page of 25 would carry 25 offers' compensation for nothing.
                 */
                field_values?: Record<string, string>;
                /**
                 * offers only, show only; the answers driving the template's conditional sections, by data key. A separate key space from field_values.
                 */
                condition_values?: Record<string, string>;
                /**
                 * offers only, show only; which controls the offer's senders moved off the value the form displayed, by key space. Where several fields map to one data key, the fields named here decide that key's sections, so the form resolves them under this report.
                 */
                edited_keys?: {
                    /**
                     * template field keys
                     */
                    field_values?: Array<string>;
                    /**
                     * condition data keys
                     */
                    condition_values?: Array<string>;
                };
            } | null;
            /**
             * openings only; the edit a live opening is holding until this request is decided, one entry per changed field. Empty for a publication, which gates the opening itself and holds nothing beside it, and empty for a caller the request does not involve — same gate as `subject`. Approving applies every `to` to the opening; rejecting discards them and the opening keeps serving the `from` values it has served throughout.
             */
            proposed_changes?: Array<{
                /**
                 * the opening attribute, e.g. `title` or `location_id`
                 */
                field?: string;
                /**
                 * the attribute named as a recruiter edits it, e.g. `Location`
                 */
                label?: string;
                /**
                 * the value the opening is serving now, rendered for display
                 */
                from?: string;
                /**
                 * the value proposed, rendered for display
                 */
                to?: string;
            }>;
            /**
             * what happened to this offer before this request, newest first. A resubmission is a fresh request with fresh steps, so every level reads pending again — this is the only thing telling an approver they are being asked a second time, and why it came back. ALWAYS EMPTY on the index: the ancestry is a primary-key walk plus a decisions read per ancestor, and nothing on a list row shows history.
             */
            previous_submissions?: Array<{
                /**
                 * 1-based from the oldest attempt
                 */
                number?: number;
                status?: 'approved' | 'rejected' | 'cancelled';
                submitted_at?: string | null;
                decided_at?: string | null;
                /**
                 * null when it was not rejected, and null throughout for a caller the request does not involve — same gate as `subject`. `status` still reads `rejected`.
                 */
                rejection_reason?: string | null;
            }>;
            viewer?: {
                /**
                 * the levels the caller may decide right now. Gate approve/reject on this rather than on `status` and `backup`. It checks the decision endpoint's identity rules but not the approve permission, so an assigned approver whose permission was revoked still gets the step listed and a 403 on deciding it. Empty once the request is settled, `cancelled` included: a cancelled request keeps its steps as the record of what happened, so an open-looking step can outlive it. Empty too for an impersonated session, which the decision endpoint refuses with 403 — an approval has to be the approver's own act.
                 */
                actionable_step_ids?: Array<number>;
                /**
                 * offers only, show only; whether the offer endpoints would accept this caller — ApplicantPolicy#manage?, which internal_api/offer_letters and internal_api/applicants/:id/offer_letter_form both gate on. NOT "may they edit": that also needs the edit permission, which a client ANDs itself. Published because an approver frequently fails applicant visibility — the same mismatch the `subject` block exists to work around for reading — so a client gating an edit control on the permission alone renders one that 403s when pressed (AXHE-4759).
                 */
                can_manage_offer?: boolean;
            };
        };
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/approvals/requests/{request_id}/approver_changes',
            path: {
                'request_id': requestId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `the caller does not hold the rule-management permission, or is impersonating`,
                404: `no such request in this account, no such step on it, or the person is not on the level`,
                409: `the approver is still in the account, was already replaced or removed, has already acted, or the level or request settled`,
                422: `the replacement is not a live user, cannot approve this subject type, or is already on the level, or a removal would leave the level with no primary approver`,
            },
        });
    }

    /**
     * list approval rules
     * @param subjectType the editor's Offers | Openings toggle
     * @param name case-insensitive substring match
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiApprovalsRules(
        subjectType?: 'OfferLetter' | 'Funnel',
        name?: string,
    ): CancelablePromise<{
        rules?: Array<{
            /**
             * external_id (UUID). Internal primary keys are not part of this API.
             */
            id?: string;
            name?: string;
            /**
             * entity-agnostic by design — one editor with an Offers | Openings toggle
             */
            subject_type?: 'OfferLetter' | 'Funnel';
            enabled?: boolean;
            available_to_sub_accounts?: boolean;
            /**
             * which subjects the rule applies to
             */
            scope_conditions?: Array<any>;
            scope_condition_logic?: 'and' | 'or';
            /**
             * so a collapsed row can name the cascade without loading `levels`
             */
            level_count?: number;
            updated_at?: string | null;
            /**
             * true when the legacy backfill owns this rule and re-syncs it in place. The editor renders it read-only and PATCH/DELETE return 409 — an edit here would be silently reverted by the next backfill run.
             */
            managed_by_legacy_group?: boolean;
            levels?: Array<{
                name?: string | null;
                /**
                 * 1-based; assigned from the submitted order, never sent by the client
                 */
                position?: number;
                /**
                 * independent of the rule's own toggle
                 */
                enabled?: boolean;
                /**
                 * any = first approval settles the level
                 */
                approver_logic?: 'any' | 'all';
                /**
                 * trigger conditions; shape varies by condition type — Approvals::ConditionsValidator is the authority
                 */
                conditions?: Array<any>;
                condition_logic?: 'and' | 'or';
                /**
                 * with escalate_unit, when backups are brought in
                 */
                escalate_after?: number | null;
                escalate_unit?: 'hours' | 'days' | null;
                approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
                /**
                 * separate from approvers: backups only apply once the escalation timer expires
                 */
                backup_approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
            }>;
        }>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/approvals/rules',
            query: {
                'subject_type': subjectType,
                'name': name,
            },
            errors: {
                403: `offer approval is not enabled for this account`,
            },
        });
    }

    /**
     * create an approval rule
     * @param requestBody
     * @returns any created
     * @throws ApiError
     */
    public static postInternalApiApprovalsRules(
        requestBody: {
            rule: {
                name: string;
                subject_type: 'OfferLetter' | 'Funnel';
                /**
                 * Optional. Omitted on create, defaults to false. Omitted on update, keeps its current value.
                 */
                enabled?: boolean;
                /**
                 * Optional. Omitted on create, defaults to false. Omitted on update, keeps its current value.
                 */
                available_to_sub_accounts?: boolean;
                /**
                 * Optional. Omitted on create, defaults to []. Omitted on update, keeps its current value. Send [] to clear it.
                 */
                scope_conditions?: Array<any>;
                /**
                 * Optional. Omitted on create, defaults to 'and'. Omitted on update, keeps its current value.
                 */
                scope_condition_logic?: 'and' | 'or';
                /**
                 * Optional. Omitted on create, defaults to []. Omitted on update, keeps its current value. Sent, it replaces the whole tree — a level absent from the array is deleted.
                 */
                levels?: Array<{
                    name?: string;
                    enabled?: boolean;
                    approver_logic?: 'any' | 'all';
                    conditions?: Array<any>;
                    condition_logic?: 'and' | 'or';
                    escalate_after?: number;
                    escalate_unit?: 'hours' | 'days';
                    approvers?: Array<{
                        approver_type?: string;
                        user_id?: string;
                        role_name?: string;
                        role_scope?: string;
                    }>;
                    backup_approvers?: Array<{
                        approver_type?: string;
                        user_id?: string;
                        role_name?: string;
                        role_scope?: string;
                    }>;
                }>;
            };
        },
    ): CancelablePromise<{
        rule?: {
            /**
             * external_id (UUID). Internal primary keys are not part of this API.
             */
            id?: string;
            name?: string;
            /**
             * entity-agnostic by design — one editor with an Offers | Openings toggle
             */
            subject_type?: 'OfferLetter' | 'Funnel';
            enabled?: boolean;
            available_to_sub_accounts?: boolean;
            /**
             * which subjects the rule applies to
             */
            scope_conditions?: Array<any>;
            scope_condition_logic?: 'and' | 'or';
            /**
             * so a collapsed row can name the cascade without loading `levels`
             */
            level_count?: number;
            updated_at?: string | null;
            /**
             * true when the legacy backfill owns this rule and re-syncs it in place. The editor renders it read-only and PATCH/DELETE return 409 — an edit here would be silently reverted by the next backfill run.
             */
            managed_by_legacy_group?: boolean;
            levels?: Array<{
                name?: string | null;
                /**
                 * 1-based; assigned from the submitted order, never sent by the client
                 */
                position?: number;
                /**
                 * independent of the rule's own toggle
                 */
                enabled?: boolean;
                /**
                 * any = first approval settles the level
                 */
                approver_logic?: 'any' | 'all';
                /**
                 * trigger conditions; shape varies by condition type — Approvals::ConditionsValidator is the authority
                 */
                conditions?: Array<any>;
                condition_logic?: 'and' | 'or';
                /**
                 * with escalate_unit, when backups are brought in
                 */
                escalate_after?: number | null;
                escalate_unit?: 'hours' | 'days' | null;
                approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
                /**
                 * separate from approvers: backups only apply once the escalation timer expires
                 */
                backup_approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
            }>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/internal_api/approvals/rules',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `the submitted document is invalid`,
            },
        });
    }

    /**
     * fetch one approval rule with its level tree
     * @param id rule external_id (UUID)
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiApprovalsRules1(
        id: string,
    ): CancelablePromise<{
        rule?: {
            /**
             * external_id (UUID). Internal primary keys are not part of this API.
             */
            id?: string;
            name?: string;
            /**
             * entity-agnostic by design — one editor with an Offers | Openings toggle
             */
            subject_type?: 'OfferLetter' | 'Funnel';
            enabled?: boolean;
            available_to_sub_accounts?: boolean;
            /**
             * which subjects the rule applies to
             */
            scope_conditions?: Array<any>;
            scope_condition_logic?: 'and' | 'or';
            /**
             * so a collapsed row can name the cascade without loading `levels`
             */
            level_count?: number;
            updated_at?: string | null;
            /**
             * true when the legacy backfill owns this rule and re-syncs it in place. The editor renders it read-only and PATCH/DELETE return 409 — an edit here would be silently reverted by the next backfill run.
             */
            managed_by_legacy_group?: boolean;
            levels?: Array<{
                name?: string | null;
                /**
                 * 1-based; assigned from the submitted order, never sent by the client
                 */
                position?: number;
                /**
                 * independent of the rule's own toggle
                 */
                enabled?: boolean;
                /**
                 * any = first approval settles the level
                 */
                approver_logic?: 'any' | 'all';
                /**
                 * trigger conditions; shape varies by condition type — Approvals::ConditionsValidator is the authority
                 */
                conditions?: Array<any>;
                condition_logic?: 'and' | 'or';
                /**
                 * with escalate_unit, when backups are brought in
                 */
                escalate_after?: number | null;
                escalate_unit?: 'hours' | 'days' | null;
                approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
                /**
                 * separate from approvers: backups only apply once the escalation timer expires
                 */
                backup_approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
            }>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/approvals/rules/{id}',
            path: {
                'id': id,
            },
            errors: {
                404: `no such rule in this account`,
            },
        });
    }

    /**
     * replace the parts of an approval rule the payload carries
     * @param id rule external_id (UUID)
     * @param requestBody
     * @returns any successful
     * @throws ApiError
     */
    public static patchInternalApiApprovalsRules(
        id: string,
        requestBody: {
            rule: {
                name: string;
                subject_type: 'OfferLetter' | 'Funnel';
                /**
                 * Optional. Omitted on create, defaults to false. Omitted on update, keeps its current value.
                 */
                enabled?: boolean;
                /**
                 * Optional. Omitted on create, defaults to false. Omitted on update, keeps its current value.
                 */
                available_to_sub_accounts?: boolean;
                /**
                 * Optional. Omitted on create, defaults to []. Omitted on update, keeps its current value. Send [] to clear it.
                 */
                scope_conditions?: Array<any>;
                /**
                 * Optional. Omitted on create, defaults to 'and'. Omitted on update, keeps its current value.
                 */
                scope_condition_logic?: 'and' | 'or';
                /**
                 * Optional. Omitted on create, defaults to []. Omitted on update, keeps its current value. Sent, it replaces the whole tree — a level absent from the array is deleted.
                 */
                levels?: Array<{
                    name?: string;
                    enabled?: boolean;
                    approver_logic?: 'any' | 'all';
                    conditions?: Array<any>;
                    condition_logic?: 'and' | 'or';
                    escalate_after?: number;
                    escalate_unit?: 'hours' | 'days';
                    approvers?: Array<{
                        approver_type?: string;
                        user_id?: string;
                        role_name?: string;
                        role_scope?: string;
                    }>;
                    backup_approvers?: Array<{
                        approver_type?: string;
                        user_id?: string;
                        role_name?: string;
                        role_scope?: string;
                    }>;
                }>;
            };
        },
    ): CancelablePromise<{
        rule?: {
            /**
             * external_id (UUID). Internal primary keys are not part of this API.
             */
            id?: string;
            name?: string;
            /**
             * entity-agnostic by design — one editor with an Offers | Openings toggle
             */
            subject_type?: 'OfferLetter' | 'Funnel';
            enabled?: boolean;
            available_to_sub_accounts?: boolean;
            /**
             * which subjects the rule applies to
             */
            scope_conditions?: Array<any>;
            scope_condition_logic?: 'and' | 'or';
            /**
             * so a collapsed row can name the cascade without loading `levels`
             */
            level_count?: number;
            updated_at?: string | null;
            /**
             * true when the legacy backfill owns this rule and re-syncs it in place. The editor renders it read-only and PATCH/DELETE return 409 — an edit here would be silently reverted by the next backfill run.
             */
            managed_by_legacy_group?: boolean;
            levels?: Array<{
                name?: string | null;
                /**
                 * 1-based; assigned from the submitted order, never sent by the client
                 */
                position?: number;
                /**
                 * independent of the rule's own toggle
                 */
                enabled?: boolean;
                /**
                 * any = first approval settles the level
                 */
                approver_logic?: 'any' | 'all';
                /**
                 * trigger conditions; shape varies by condition type — Approvals::ConditionsValidator is the authority
                 */
                conditions?: Array<any>;
                condition_logic?: 'and' | 'or';
                /**
                 * with escalate_unit, when backups are brought in
                 */
                escalate_after?: number | null;
                escalate_unit?: 'hours' | 'days' | null;
                approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
                /**
                 * separate from approvers: backups only apply once the escalation timer expires
                 */
                backup_approvers?: Array<{
                    /**
                     * a named user, or a role resolved at submission
                     */
                    approver_type?: 'user' | 'role';
                    /**
                     * User external_id (UUID); null for a role approver
                     */
                    user_id?: string | null;
                    /**
                     * display only — the client round-trips user_id. Survives a soft-deleted user.
                     */
                    user_name?: string | null;
                    role_name?: string | null;
                    /**
                     * where the role is resolved from, e.g. the opening
                     */
                    role_scope?: string | null;
                }>;
            }>;
        };
    }> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/internal_api/approvals/rules/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                409: `the rule is managed by the legacy backfill`,
                422: `the submitted document is invalid`,
            },
        });
    }

    /**
     * soft-delete an approval rule, its levels, and the requests it was gating
     * @param id rule external_id (UUID)
     * @returns any successful
     * @throws ApiError
     */
    public static deleteInternalApiApprovalsRules(
        id: string,
    ): CancelablePromise<{
        success?: boolean;
        /**
         * pending requests this delete cancelled
         */
        cancelled_requests?: number;
    }> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/internal_api/approvals/rules/{id}',
            path: {
                'id': id,
            },
            errors: {
                409: `the rule is managed by the legacy backfill`,
            },
        });
    }

    /**
     * count the pending approval requests a delete would cancel
     * @param id rule external_id (UUID)
     * @returns any successful
     * @throws ApiError
     */
    public static getInternalApiApprovalsRulesPendingRequests(
        id: string,
    ): CancelablePromise<{
        /**
         * pending requests deleting this rule would cancel
         */
        pending_requests?: number;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/internal_api/approvals/rules/{id}/pending_requests',
            path: {
                'id': id,
            },
            errors: {
                404: `no such rule in this account`,
            },
        });
    }

}